import QueryBoundary from "components/QueryBoundary/QueryBoundary";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { ToastContextType, useToast } from "contexts/ToastContext";
import { useUser } from "contexts/UserContext";
import { EnhancedUser, hasRole } from "services/users/utils";
import { ForbiddenPage } from "pages/Error/ForbiddenPage";
import React from "react";
import { Location, NavigateFunction, Params, useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import { UserRole } from "services/users/model";
type Props = {
  Component: React.ComponentType<InjectedProps>;
  permissions?: Permissions;
  notAllowedElement?: React.ReactElement;

  // Passthrough props
  mode?: "view" | "edit";
};

type Permissions = UserRole | UserRole[] | ((user: EnhancedUser) => boolean);

export default function RestrictedRoute({
  Component,
  permissions,
  notAllowedElement = <ForbiddenPage />,
  ...rest
}: Props) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();

  const { showToast } = useToast();

  if (!hasPermissions(user, permissions)) {
    return notAllowedElement;
  }

  const injectedProps = {
    params,
    location,
    navigate,
    showToast,
    ...rest,
  };

  const fallback = (
    <div className="page-spinner-container">
      <TalaeraSpinner className="page-spinner" />
    </div>
  );

  return (
    <QueryBoundary fallback={fallback}>
      <Component {...injectedProps} />
    </QueryBoundary>
  );
}

// [deprecated] compatibility shim for legacy class components
type InjectedProps = {
  params: Params<any>;
  location: Location;
  navigate: NavigateFunction;
  showToast: ToastContextType["showToast"];
  mode?: "view" | "edit";
};

const hasPermissions = (user: EnhancedUser, permissions?: Permissions) => {
  if (Array.isArray(permissions)) {
    return permissions.some(p => hasRole(p));
  } else if (typeof permissions === "string") {
    return hasRole(permissions);
  } else if (permissions) {
    return permissions(user);
  } else {
    return true;
  }
};
